import {BaseFactory} from "../../../infrastructure/factories/base-factory";
import {H2hResponseApiModel} from "../models/h2h.response-api-model";
import {H2hDataSource} from "../models/h2h.data-source";
import {BaseService} from "../../../infrastructure/domain-services/base-service";
import {LoggerUtils} from "../../../infrastructure/utils/logger/logger-utils";
import {TermsUtils} from "../../../infrastructure/utils/terms-utils";
import {CountryDomainModel} from "../../common/country/models/country-domain-model";
import {CompetitionDomainModel} from "../../common/competition/models/competition-domain-model";
import {PartialGameDomainModel} from "../../partial-game/models/partial-game-domain-model";
import {SportDomainModel} from "../../sports/models/sport-domain-model";
import {DatetimeUtils} from "../../../infrastructure/utils/datetime-utils";
import {GameH2hDomainModel} from "../models/game/game-h2h.domain-model";
import {GameH2hApiModel} from "../models/game/game-h2h.api-model";
import {GameH2hCompetitorDomainModel} from "../models/competitor/game-h2h.competitor.domain-model";
import {GameCompetitorDomainModel} from "../../partial-game/models/game-competitor/game-competitor-domain-model";
import {CompetitionFactory} from "../../common/competition/domain-services/competition-factory";
import {OddsDomainModel} from "../../common/odds/models/odds.domain-model";
import {BookmakerApiModel} from "../../common/bookmaker/models/bookmaker-api-model";
import {OddsFactory} from "../../common/odds/domain-services/odds.factory";
import {GameH2hCompetitorApiModel} from "../models/competitor/game-h2h.competitor.api-model";

export class H2hFactory extends BaseFactory {
  datetimeUtils: DatetimeUtils;

  static injectables = [...BaseService.injectables, LoggerUtils, TermsUtils, CompetitionFactory, DatetimeUtils, OddsFactory];

  constructor(
    logger: LoggerUtils,
    terms: TermsUtils,
    competitionFactory: CompetitionFactory,
    datetimeUtils: DatetimeUtils,
    oddsFactory: OddsFactory
  ) {
    super(logger, terms);

    this.competitionFactory = competitionFactory;
    this.datetimeUtils = datetimeUtils;
    this.oddsFactory = oddsFactory;
  }


  createResponse(apiModel: H2hResponseApiModel): H2hResponseApiModel {
    const responseApiModel: H2hResponseApiModel = apiModel && new H2hResponseApiModel(apiModel);

    return responseApiModel;
  }

  createDomainModel(response: H2hResponseApiModel): H2hDataSource {
    if (!response) {
      return null;
    }
    const sportsMap: Map<Number, SportDomainModel> = this.createDomainMap(response.sports, SportDomainModel);
    const countryMap: Map<Number, CountryDomainModel> = this.createDomainMap(response.countries, CountryDomainModel);
    const competitionsMap: Map<Number, CompetitionDomainModel> = this.competitionFactory.createDomainMap(response.competitions, CompetitionDomainModel, 'id', response.sports, response.countries);
    const bookmakersArray: Array<BookmakerApiModel> = response.bookmakers;
    const game: GameH2hDomainModel = this.createGameDomainObject(response.game, competitionsMap, countryMap, sportsMap, bookmakersArray);

    const h2hDataSource: H2hDataSource = new H2hDataSource(response, game);

    return h2hDataSource;
  }

  createGameDomainObject(apiModel: GameH2hApiModel, competitionsMap: Map<Number, CompetitionDomainModel>, countryMap: Map<Number, CountryDomainModel>, sportsMap: Map<Number, SportDomainModel>, bookmakersArray: Array<BookmakerApiModel>): GameH2hDomainModel {
    const {
      competition,
      homeCompetitor,
      awayCompetitor,
      startTime,
      sport
    }: PartialGameDomainModel = this.createPartialGameDomainModel(apiModel, competitionsMap, countryMap, sportsMap, true)
    const h2hGames: Array<PartialGameDomainModel> = apiModel?.h2hGames?.map((game: PartialGameDomainModel) => this.createPartialGameDomainModel(game, competitionsMap, countryMap, sportsMap, false, bookmakersArray));

    const game: GameH2hDomainModel = new GameH2hDomainModel(apiModel, competition, homeCompetitor, awayCompetitor, startTime, sport, h2hGames);

    return game;
  }

  createPartialGameDomainModel(game: GameH2hApiModel, competitionsMap: Map<Number, CompetitionDomainModel>, countryMap: Map<Number, CountryDomainModel>, sportsMap: Map<Number, SportDomainModel>, isPrimaryGame: Boolean, bookmakersApiModel: Array<BookmakerApiModel>) {
    const competition: CompetitionDomainModel = competitionsMap.get(game.competitionId);
    const sport: SportDomainModel = sportsMap.get(game.sportId);

    const homeCompetitor: GameH2hCompetitorDomainModel = this.createCompetitorDomainModel(game.homeCompetitor, competitionsMap, countryMap, sportsMap, isPrimaryGame, bookmakersApiModel);
    const awayCompetitor: GameH2hCompetitorDomainModel = this.createCompetitorDomainModel(game.awayCompetitor, competitionsMap, countryMap, sportsMap, isPrimaryGame, bookmakersApiModel);
    const startTime: Date = this.datetimeUtils.fromISO(game.startTime);
    const odds: OddsDomainModel = game.odds && this.oddsFactory.createDomainModel(game.odds, bookmakersApiModel);

    return new PartialGameDomainModel(game, competition, homeCompetitor, awayCompetitor, startTime, sport, odds)
  }

  createCompetitorDomainModel(competitor: GameH2hCompetitorApiModel, competitionsMap: Map<Number, CompetitionDomainModel>, countryMap: Map<Number, CountryDomainModel>, sportsMap: Map<Number, SportDomainModel>, isPrimaryGame: Boolean, bookmakers: Array<BookmakerApiModel>): GameH2hCompetitorDomainModel {
    const competitorCountryDomain: CountryDomainModel = countryMap.get(competitor.countryId);
    const sport: SportDomainModel = sportsMap.get(competitor.sportId);

    const recentGames: Array<PartialGameDomainModel> = competitor?.recentGames?.map((game: PartialGameDomainModel) => this.createPartialGameDomainModel(game, competitionsMap, countryMap, sportsMap, isPrimaryGame, bookmakers));

    const type = isPrimaryGame ? GameH2hCompetitorDomainModel : GameCompetitorDomainModel
    return new type(competitor, competitorCountryDomain, sport, recentGames);
  }
}
